import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
} from "react";

import { useSelector } from "react-redux";
import { testId } from '../../../../utils/testIds';
import { useFormContext } from "react-hook-form";
import get from "lodash/get";

import Icon from "../../../../icons";

const DEFAULT_MAX_ERROR = "Maximum selections reached.";
const DUPLICATE_TAG_ERROR = "Cannot add duplicate, Tag already exists";

function TagField({
  config,
  value,
  onChange,
  onBlur,
  disabled,
  required,
  error,
}) {
  const {
    fieldName,
    fieldKey,
    label,
    isRequired,
    description,
    items,
    fieldSettings,
  } = config || {};

  const optionsFromStorePath = fieldSettings?.optionsFromStore;
  const allowCustomTags = fieldSettings?.allowCustomTags ?? false;

  const optionsFromStore = useSelector((state) =>
    optionsFromStorePath
      ? get(state, optionsFromStorePath, [])
      : null
  );

  const options = useMemo(() => {
    if (Array.isArray(items) && items.length > 0) {
      return items.map((it) => ({
        id: it.id ?? it.key ?? it.value,
        text: it.text ?? it.label ?? it.value ?? String(it.id ?? ""),
      }));
    }
    return Array.isArray(optionsFromStore) ? optionsFromStore : [];
  }, [items, optionsFromStore]);

  const maxTags = fieldSettings?.maxTags ?? Infinity;
  const maxError = fieldSettings?.maxTagsError ?? DEFAULT_MAX_ERROR;
  const placeholder = fieldSettings?.placeholder ?? "Search";

  const [inputValue, setInputValue] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(null);
  const [maxErrorMsg, setMaxErrorMsg] = useState("");
  const [duplicateErrorMsg, setDuplicateErrorMsg] = useState("");
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const formContext = useFormContext();
  const isSubmitSuccessful = formContext?.formState?.isSubmitSuccessful;

  useEffect(() => {
    if (isSubmitSuccessful) {
      setDuplicateErrorMsg("");
    }
  }, [isSubmitSuccessful]);

  const selectedItems = Array.isArray(value) ? value : [];

  const filteredOptions = useMemo(() => {
    const q = inputValue.trim().toLowerCase();
    return (options || []).filter(
      (opt) =>
        !selectedItems.some((s) => s.id === opt.id) &&
        (
          q
            ? String(opt.text).toLowerCase().includes(q)
            : true
        )
    );
  }, [options, inputValue, selectedItems]);

  const showCreateNew = useMemo(() => {
    if (!allowCustomTags) return false;
    const trimmed = inputValue.trim();
    if (!trimmed) return false;
    const exactMatchInSelected = selectedItems.some(
      (s) => String(s.text) === trimmed
    );
    const exactMatchInOptions = options.some(
      (o) => String(o.text) === trimmed
    );
    return !exactMatchInSelected && !exactMatchInOptions;
  }, [allowCustomTags, inputValue, options, selectedItems]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
        setHighlightedIndex(null);
      }
    };

    document.addEventListener(
      "mousedown",
      handleClickOutside
    );

    return () => {
      document.removeEventListener(
        "mousedown",
        handleClickOutside
      );
    };
  }, []);

  const selectOption = (option) => {
    const isDuplicate = selectedItems.some(
      (s) => String(s.text).trim() === String(option.text).trim()
    );
    if (isDuplicate) {
      setDuplicateErrorMsg(DUPLICATE_TAG_ERROR);
      setIsOpen(false);
      return;
    }

    if (selectedItems.length >= maxTags) {
      setMaxErrorMsg(maxError);
      setIsOpen(false);
      return;
    }

    setMaxErrorMsg("");
    setDuplicateErrorMsg("");

    const nextValue = [
      ...selectedItems,
      option,
    ];
    onChange(nextValue);

    setInputValue("");
    setHighlightedIndex(null);
    setIsOpen(false);
  };

  const removeItem = (idx) => {
    const next = selectedItems.filter(
      (_, i) => i !== idx
    );

    onChange(next);
    setMaxErrorMsg("");
    setDuplicateErrorMsg("");
  };

  const createNewTag = () => {
    const trimmedValue = inputValue.trim();
    if (!trimmedValue) return;

    const newTag = {
      id: trimmedValue,
      text: trimmedValue,
    };

    selectOption(newTag);
  };

  const commitTypedValue = () => {
    const trimmedValue = inputValue.trim();
    if (!trimmedValue) return;

    const exactMatch = filteredOptions.find(
      (opt) => String(opt.text).toLowerCase() === trimmedValue.toLowerCase()
    );

    if (exactMatch) {
      selectOption(exactMatch);
    } else if (allowCustomTags) {
      createNewTag();
    }
  };

  const handleKeyDown = (e) => {
    if (e.keyCode === 40 || e.keyCode === 38) {
      e.preventDefault();

      const totalOptions = filteredOptions.length + (showCreateNew ? 1 : 0);

      if (totalOptions === 0) {
        return;
      }

      const next =
        e.keyCode === 40
          ? (
              highlightedIndex === null ||
              highlightedIndex === totalOptions - 1
                ? 0
                : highlightedIndex + 1
            )
          : (
              highlightedIndex === null ||
              highlightedIndex === 0
                ? totalOptions - 1
                : highlightedIndex - 1
            );

      setHighlightedIndex(next);
    } else if (e.keyCode === 13) {
      e.preventDefault();

      if (
        highlightedIndex !== null &&
        filteredOptions[highlightedIndex]
      ) {
        selectOption(
          filteredOptions[highlightedIndex]
        );
      } else if (
        showCreateNew &&
        (highlightedIndex === null || highlightedIndex === filteredOptions.length)
      ) {
        createNewTag();
      } else if (inputValue.trim()) {
        commitTypedValue();
      }
    } else if (
      e.keyCode === 8 &&
      !inputValue.trim() &&
      selectedItems.length > 0
    ) {
      removeItem(selectedItems.length - 1);
    } else if (e.keyCode === 27) {
      setIsOpen(false);
      setHighlightedIndex(null);
    }
  };

  return (
    <div className={`formGroup tag-field${disabled ? ' disabled' : ''}`}>
      <div className="wth-2">
        {label ? (
          <label htmlFor={fieldKey}>
            {label}
            {(required || isRequired) ? <sup>*</sup> : null}
          </label>
        ) : null}
      </div>
      <div className="wth-9 formTags">
        <div
          ref={containerRef}
          onMouseDown={(e) => {
            if (e.target !== inputRef.current) {
              e.preventDefault();
            }
          }}
        >
          <div className="ReactTags__tags react-tags-wrapper">
            <div
              className="ReactTags__selected"
              onClick={(e) => {
                if (e.target === inputRef.current) {
                  return;
                }
                if (inputValue.trim()) {
                  setIsOpen((prev) => !prev);
                }
                inputRef.current?.focus();
              }}
            >
              {selectedItems.map((item, idx) => (
                <span
                  key={item.id}
                  className="tag-wrapper ReactTags__tag"
                >
                  {item.text}
                  <span
                    className="ReactTags__remove"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeItem(idx);
                    }}
                  >
                    {!disabled && (
                      <Icon
                        name="close"
                        className="icon"
                      />
                    )}
                  </span>
                </span>
              ))}
              <div className="ReactTags__tagInput">
                <input
                  ref={inputRef}
                  data-test={testId('field', config.fieldKey, 'input')}
                  className="ReactTags__tagInputField"
                  type="text"
                  placeholder={
                    selectedItems.length === 0
                      ? placeholder
                      : ""
                  }
                  value={inputValue}
                  disabled={disabled}
                  onChange={(e) => {
                    const nextValue = e.target.value;
                    setInputValue(nextValue);
                    setIsOpen(Boolean(nextValue.trim()));
                    setMaxErrorMsg("");
                    setDuplicateErrorMsg("");
                  }}
                  onBlur={(e) => {
                    commitTypedValue();
                    onBlur(e);
                  }}
                  onKeyDown={handleKeyDown}
                />
              </div>
            </div>
          </div>
        </div>
        {isOpen && !disabled && (filteredOptions.length > 0 || showCreateNew) ? (
          <div className="mailDropdown select serviceLocation">
            <div className="mailingSearch serviceLocation openTop onClick">
              <div className="select2-results">
                {filteredOptions.map((item, index) => (
                  <a
                    className={
                      highlightedIndex === index
                        ? "activeItem"
                        : ""
                    }
                    key={item.id}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      selectOption(item);
                    }}
                    onMouseEnter={() =>
                      setHighlightedIndex(index)
                    }
                    onMouseLeave={() =>
                      setHighlightedIndex(null)
                    }
                  >
                    <div className="drop-down-block">
                      <span className="tag-name">
                        {item.text}
                      </span>
                    </div>
                  </a>
                ))}
                {showCreateNew ? (
                  <a
                    className={
                      highlightedIndex === filteredOptions.length
                        ? "activeItem"
                        : ""
                    }
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      createNewTag();
                    }}
                    onMouseEnter={() =>
                      setHighlightedIndex(filteredOptions.length)
                    }
                    onMouseLeave={() =>
                      setHighlightedIndex(null)
                    }
                  >
                    <div className="drop-down-block">
                      <span className="tag-name">
                        Create "{inputValue.trim()}"
                      </span>
                    </div>
                  </a>
                ) : null}
              </div>
            </div>
          </div>
        ) : null}
        <div className="formHelperText">{description}</div>

        {maxErrorMsg ? (
          <span className="max-skillset-error">
            {maxErrorMsg}
          </span>
        ) : null}
        {duplicateErrorMsg ? (
          <span className="max-skillset-error">
            {duplicateErrorMsg}
          </span>
        ) : null}
        {error ? (
          <span className="errorSelfMsg">
            {error}
          </span>
        ) : null}
      </div>
    </div>
  );
}

export default TagField;